/**
 * Admin API wrapper. All calls send cookies via `credentials: 'include'`
 * so the `act_admin` httpOnly JWT cookie is included.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

async function parseErr(res) {
    try {
        const body = await res.json();
        if (typeof body.detail === "string") return body.detail;
        if (Array.isArray(body.detail))
            return body.detail
                .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
                .join(" ");
        return JSON.stringify(body);
    } catch {
        return res.statusText || "Request failed";
    }
}

async function doFetch(path, options = {}) {
    const res = await fetch(`${API}${path}`, {
        credentials: "include",
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = new Error(await parseErr(res));
        err.status = res.status;
        throw err;
    }
    if (res.status === 204) return null;
    return res.json();
}

export const adminApi = {
    async login(email, password) {
        return doFetch("/login", {
            method: "POST",
            body: JSON.stringify({ email, password }),
        });
    },
    async logout() {
        return doFetch("/logout", { method: "POST" });
    },
    async me() {
        return doFetch("/me", { method: "GET" });
    },
    /**
     * Same as me() but retries ONCE after 300ms on a 401. Use inside route
     * guards to absorb a small cookie-attach race in some browsers (Safari,
     * strict Firefox profiles, iframe contexts) where the just-set cookie
     * is not immediately visible to the next XHR. Throws the final error.
     */
    async meWithRetry() {
        try {
            return await this.me();
        } catch (err) {
            if (err.status !== 401) throw err;
            await new Promise((r) => setTimeout(r, 300));
            return await this.me();
        }
    },
    async stats() {
        return doFetch("/stats", { method: "GET" });
    },
    async listInquiries({ q = "", handled, limit = 50, offset = 0 } = {}) {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (handled !== undefined && handled !== null)
            p.set("handled", String(handled));
        p.set("limit", String(limit));
        p.set("offset", String(offset));
        return doFetch(`/inquiries?${p}`);
    },
    async updateInquiry(id, payload) {
        return doFetch(`/inquiries/${id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
        });
    },
    async listCallbacks({ q = "", handled, limit = 50, offset = 0 } = {}) {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (handled !== undefined && handled !== null)
            p.set("handled", String(handled));
        p.set("limit", String(limit));
        p.set("offset", String(offset));
        return doFetch(`/callbacks?${p}`);
    },
    async updateCallback(id, payload) {
        return doFetch(`/callbacks/${id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
        });
    },
    async listNewsletter({ q = "", limit = 50, offset = 0 } = {}) {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        p.set("limit", String(limit));
        p.set("offset", String(offset));
        return doFetch(`/newsletter?${p}`);
    },
    async listNdaAccess({ q = "", limit = 50, offset = 0 } = {}) {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        p.set("limit", String(limit));
        p.set("offset", String(offset));
        return doFetch(`/nda-access?${p}`);
    },
    exportCsvUrl(kind, { q = "", handled } = {}) {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (handled !== undefined && handled !== null)
            p.set("handled", String(handled));
        const qs = p.toString();
        return `${API}/${kind}/export.csv${qs ? `?${qs}` : ""}`;
    },
    async downloadCsv(kind, opts = {}) {
        const res = await fetch(adminApi.exportCsvUrl(kind, opts), {
            credentials: "include",
        });
        if (!res.ok) {
            const err = new Error(await parseErr(res));
            err.status = res.status;
            throw err;
        }
        const blob = await res.blob();
        const dispo = res.headers.get("Content-Disposition") || "";
        const match = /filename="([^"]+)"/.exec(dispo);
        const filename = match ? match[1] : `act-${kind}.csv`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    },

    // -------------------------------------------------------------------
    // Invoices (Phase 1c)
    // -------------------------------------------------------------------
    async listInvoices({ q = "", status, limit = 50, skip = 0 } = {}) {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status) p.set("status", status);
        p.set("limit", String(limit));
        p.set("skip", String(skip));
        return doFetch(`/invoices?${p}`);
    },
    async getInvoice(id) {
        return doFetch(`/invoices/${id}`);
    },
    async createInvoice(payload) {
        return doFetch("/invoices", {
            method: "POST",
            body: JSON.stringify(payload),
        });
    },
    async updateInvoice(id, payload) {
        return doFetch(`/invoices/${id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
        });
    },
    async resendInvoice(id) {
        return doFetch(`/invoices/${id}/resend`, { method: "POST" });
    },
    invoicePdfUrl(id) {
        return `${API}/invoices/${id}/pdf`;
    },
    async downloadInvoicePdf(id, invoiceNumber) {
        const res = await fetch(adminApi.invoicePdfUrl(id), {
            credentials: "include",
        });
        if (!res.ok) {
            const err = new Error(await parseErr(res));
            err.status = res.status;
            throw err;
        }
        const blob = await res.blob();
        const filename = invoiceNumber
            ? `ACT-${invoiceNumber}.pdf`
            : `ACT-invoice-${id}.pdf`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    },

    // -------------------------------------------------------------------
    // Admin users (Phase C — owner-only)
    // -------------------------------------------------------------------
    async listAdminUsers() {
        return doFetch("/users");
    },
    async createAdminUser({ email, password, role }) {
        return doFetch("/users", {
            method: "POST",
            body: JSON.stringify({ email, password, role }),
        });
    },
    async updateAdminUser(id, { role, password } = {}) {
        const body = {};
        if (role) body.role = role;
        if (password) body.password = password;
        return doFetch(`/users/${id}`, {
            method: "PATCH",
            body: JSON.stringify(body),
        });
    },
    async deleteAdminUser(id) {
        return doFetch(`/users/${id}`, { method: "DELETE" });
    },

    // -------------------------------------------------------------------
    // Private-portfolio passcodes (Phase B — owner + editor)
    // -------------------------------------------------------------------
    async listPasscodes() {
        return doFetch("/passcodes");
    },
    async createPasscode({ code, label, expires_at } = {}) {
        const body = {};
        if (code) body.code = code;
        if (label) body.label = label;
        if (expires_at) body.expires_at = expires_at;
        return doFetch("/passcodes", {
            method: "POST",
            body: JSON.stringify(body),
        });
    },
    async patchPasscode(id, payload) {
        return doFetch(`/passcodes/${id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
        });
    },
    async revokePasscode(id) {
        return doFetch(`/passcodes/${id}`, { method: "DELETE" });
    },
    async restorePasscode(id) {
        return doFetch(`/passcodes/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ restore: true }),
        });
    },

    // -------------------------------------------------------------------
    // Proposals (Phase A — proposal builder)
    // -------------------------------------------------------------------
    async listProposals({ q = "", status, limit = 50, skip = 0 } = {}) {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status) p.set("status", status);
        p.set("limit", String(limit));
        p.set("skip", String(skip));
        return doFetch(`/proposals?${p}`);
    },
    async getProposal(id) {
        return doFetch(`/proposals/${id}`);
    },
    async createProposal(payload) {
        return doFetch("/proposals", {
            method: "POST",
            body: JSON.stringify(payload),
        });
    },
    async updateProposal(id, payload) {
        return doFetch(`/proposals/${id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
        });
    },
    async deleteProposal(id) {
        return doFetch(`/proposals/${id}`, { method: "DELETE" });
    },
    async aiGenerate(section, context) {
        return doFetch("/proposals/ai-generate", {
            method: "POST",
            body: JSON.stringify({ section, context }),
        });
    },
    proposalPdfUrl(id) {
        return `${API}/proposals/${id}/pdf`;
    },
    async downloadProposalPdf(id, proposalNumber, clientName) {
        const res = await fetch(adminApi.proposalPdfUrl(id), {
            credentials: "include",
        });
        if (!res.ok) {
            const err = new Error(await parseErr(res));
            err.status = res.status;
            throw err;
        }
        const blob = await res.blob();
        const dispo = res.headers.get("Content-Disposition") || "";
        const match = /filename="([^"]+)"/.exec(dispo);
        const filename = match
            ? match[1]
            : `${proposalNumber || "proposal"}-${(clientName || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    },
};
